import { loginWithEmail, refreshIdToken, signOut } from "../common/firebase/AuthService"
import { fetchAdminAccountByEmail, fetchAdminAccountByEmailAndUsername, fetchAdminAccountByUsername, fetchLatestEmailAuth, verifyEmailAuth } from "../common/firebase/Firestore"
import { authStore } from "../store/AuthStore"
import { UserCredential } from "firebase/auth";
import { resetPassword, sendAuthenticationMail, sendFindUsernameEmail, verifyIdToken } from "../common/firebase/Function";
import { v4 as uuidv4 } from "uuid";

/**
 * 사용자 ID / 비밀번호 로그인
 * @async
 * @function loginWithUsername
 * @param {string} username - 사용자 ID
 * @param {string} password - 비밀번호
 * @returns {Promise<UserCredential>}
 * @throws {Error}
 */
const loginWithUsername = async (username: string, password: string): Promise<UserCredential> => {
    try {
        const adminAccount = await fetchAdminAccountByUsername(username);

        if (!adminAccount) {
            throw new Error("해당 userId를 가진 관리자 계정이 존재하지 않습니다.");
        }

        console.log("조회된 adminAccount 정보: ", adminAccount);

        const userCredential = await loginWithEmail(adminAccount.email, password);
        const idToken = await userCredential.user.getIdToken();
        authStore.setLogin(adminAccount, userCredential, idToken);

        return userCredential;
    } catch (error) {
        throw error;
    }
};

const refreshToken = async(): Promise<string> => {
    const idToken = await refreshIdToken();
    authStore.setIdToken(idToken);

    return idToken;
}

const verifyAuthentication = async (): Promise<boolean> => {
    const currentIdToken = authStore.getIdToken();

    if (currentIdToken === null) {
        return false;
    }

    try {
        return await verifyIdToken(currentIdToken);
    } catch (error) {
        console.error("[verifyAuthentication] verifyIdToken failed, treating token as invalid:", error);
        // 토큰이 만료되었거나 서버에서 유효하지 않다고 판단한 경우
        // 여기서는 단순히 "유효하지 않음"으로 처리하고 상위에서 refreshToken을 시도하도록 false를 반환한다.
        return false;
    }
}

const signOutFromFirebase = async () => {
    await signOut();
}

const requestAuthenticationEmail = async (email: string, username: string, needsAccountCheck: boolean): Promise<boolean> => {
    if (needsAccountCheck) {
        const adminAccount = await fetchAdminAccountByEmailAndUsername(email, username);
        
        if (adminAccount) {
            await sendAuthenticationMail(email);
            return true;
        } else {
            return false;
        }
    } else {
        await sendAuthenticationMail(email);
        return true;
    }
}

const requestFindUsernameEmail = async (email: string): Promise<boolean> => {
    const adminAccount = await fetchAdminAccountByEmail(email);

    if (adminAccount) {
        await sendFindUsernameEmail(email, adminAccount.username);
        return true;
    } else {
        return false;
    }
}

/**
 * 이메일 인증번호 검증
 * @async
 * @function validateAuthCode
 * @param {string} email - 이메일
 * @param {string} authCode - 인증번호
 * @returns {Promise<boolean>}
 */
const validateAuthCode = async (email: string, authCode: string): Promise<boolean> => {
    const latestEmailAuth = await fetchLatestEmailAuth(email);

    if (!latestEmailAuth) {
        return false;
    }

    const now = new Date();
    const isNotExpired = now <= latestEmailAuth.expiredAt;
    const isAuthCodeMatch = latestEmailAuth.authCode === authCode;
    /// 기인증 완료 여부
    const isVerified = latestEmailAuth.verified;

    if (isNotExpired && isAuthCodeMatch && !isVerified) {
        const verificationId = uuidv4();
        await verifyEmailAuth(latestEmailAuth.id, verificationId);
        /// 비밀번호 변경을 위한 임시 인증 저장
        authStore.saveChangePasswordVerificationId(verificationId);
        
        return true;
    } else {
        return false;
    }
}

const requestResetPassword = async (email: string, password: string, verificationId: string): Promise<void> => {
    await resetPassword(email, password, verificationId);
}

export { loginWithUsername, refreshToken, verifyAuthentication, signOutFromFirebase, requestAuthenticationEmail, requestFindUsernameEmail, validateAuthCode, requestResetPassword };